'use client';

import { useState } from 'react';
import { Modal } from '@shared/ui';
import type { BaseModalProps } from '@shared/types/modal.types';

interface SelectTimeModalProps extends BaseModalProps {
  onTimeSelected?: (selectedTime: {
    date: string;
    time: string;
    datetime: Date;
  }) => void;
  minDate?: string;
  maxDate?: string;
  title?: string;
}

const TIME_SLOTS = [
  '08:00',
  '09:00',
  '10:00',
  '11:00',
  '12:00',
  '13:00',
  '14:00',
  '15:00',
  '16:00',
  '17:00',
  '18:00',
  '19:00',
  '20:00',
];

/**
 * Select Time Modal - allows users to pick a date and time slot
 * Usage: const { open } = useModal('select-time'); open({ onTimeSelected: (time) => {...} });
 */
export function SelectTimeModal({
  isOpen,
  onClose,
  onTimeSelected,
  minDate,
  maxDate,
  title = 'Select Date & Time',
}: SelectTimeModalProps) {
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState<string>(minDate || today);
  const [selectedTime, setSelectedTime] = useState<string>('');

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
    setSelectedTime(''); // Reset time when date changes
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
  };

  const handleConfirm = () => {
    if (!selectedDate || !selectedTime) return;

    const [hours, minutes] = selectedTime.split(':').map(Number);
    const datetime = new Date(selectedDate);
    datetime.setHours(hours, minutes, 0, 0);

    onTimeSelected?.({
      date: selectedDate,
      time: selectedTime,
      datetime,
    });

    onClose();
    setSelectedDate(minDate || today);
    setSelectedTime('');
  };

  // Check if a time slot is in the past for today's date
  const isTimeSlotPast = (timeSlot: string): boolean => {
    if (selectedDate !== today) return false;

    const now = new Date();
    const [hours, minutes] = timeSlot.split(':').map(Number);
    const slotTime = new Date();
    slotTime.setHours(hours, minutes, 0, 0);

    return slotTime <= now;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      animation="slide-up"
      title={title}
      description="Choose your preferred date and time"
    >
      <div className="space-y-6">
        {/* Date Picker */}
        <div>
          <label
            htmlFor="date"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Select Date
          </label>
          <input
            type="date"
            id="date"
            value={selectedDate}
            onChange={handleDateChange}
            min={minDate || today}
            max={maxDate}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Time Slots */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Time
          </label>
          <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto p-1">
            {TIME_SLOTS.map((timeSlot) => {
              const isPast = isTimeSlotPast(timeSlot);
              const isSelected = selectedTime === timeSlot;

              return (
                <button
                  key={timeSlot}
                  type="button"
                  onClick={() => handleTimeSelect(timeSlot)}
                  disabled={isPast}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    isSelected
                      ? 'bg-blue-600 text-white ring-2 ring-blue-600 ring-offset-2'
                      : isPast
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white border border-gray-300 text-gray-700 hover:border-blue-500 hover:bg-blue-50'
                  }`}
                >
                  {timeSlot}
                </button>
              );
            })}
          </div>
          {selectedDate === today && (
            <p className="mt-2 text-xs text-gray-500">
              Past time slots are disabled
            </p>
          )}
        </div>

        {/* Selected summary */}
        {selectedDate && selectedTime && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-blue-900">
              Selected Time:
            </p>
            <p className="text-lg font-semibold text-blue-700 mt-1">
              {new Date(selectedDate).toLocaleDateString('en-CA', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}{' '}
              at {selectedTime}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedDate || !selectedTime}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </Modal>
  );
}
